import mammoth from "mammoth";
import { extractAltChunkContent } from "../ooxml/extractAltChunk";
import { extractNumberingFromDocx } from "../ooxml/extractNumbering";
import { extractParagraphMetaFromDocx } from "../ooxml/extractParagraphs";
import { extractTableMetaFromDocx } from "../ooxml/extractTables";
import { extractTextMetaFromDocx } from "../ooxml/extractText";
import { applyNumberingToHtml } from "../merge/applyNumberingMeta";
import { applyParagraphMetaToHtml } from "../merge/applyParagraphMeta";
import { applyTableMetaToHtml } from "../merge/applyTableMeta";
import { applyTextMetaToHtml } from "../merge/applyTextMeta";
import { MAMMOTH_STYLE_MAP } from "../mammoth/styleMap";
import { transformDocumentForAlignment } from "../mammoth/transforms";
import type {
  DocxConversionResult,
  NumberingFidelityStats,
  ParagraphFidelityStats,
  TableFidelityStats,
  TextFidelityStats,
} from "../types";

const EMPTY_PARAGRAPH_STATS: ParagraphFidelityStats = {
  paragraphsInDocx: 0,
  paragraphsInHtml: 0,
  paragraphsEnriched: 0,
  appliedAlignment: 0,
  appliedIndent: 0,
  appliedSpacing: 0,
  appliedLineHeight: 0,
};

const EMPTY_NUMBERING_STATS: NumberingFidelityStats = {
  paragraphsInDocx: 0,
  paragraphsInHtml: 0,
  numberedInDocx: 0,
  numbersApplied: 0,
  listsStructured: 0,
  multilevelApplied: 0,
};

const EMPTY_TABLE_STATS: TableFidelityStats = {
  tablesInDocx: 0,
  tablesInHtml: 0,
  tablesEnriched: 0,
  cellsEnriched: 0,
  bordersApplied: 0,
  mergesApplied: 0,
  widthsApplied: 0,
  alignmentsApplied: 0,
};

const EMPTY_TEXT_STATS: TextFidelityStats = {
  paragraphsInDocx: 0,
  paragraphsInHtml: 0,
  paragraphsEnriched: 0,
  runsRebuilt: 0,
  semanticTagsNormalized: 0,
};

/**
 * DOCX → HTML fidelity pipeline:
 * mammoth + OOXML text, paragraph, numbering, and table supplements.
 */
export async function convertDocxWithParagraphFidelity(
  arrayBuffer: ArrayBuffer,
): Promise<DocxConversionResult> {
  const mammothInput: { arrayBuffer: ArrayBuffer; buffer?: Buffer } = {
    arrayBuffer,
  };
  if (typeof Buffer !== "undefined") {
    mammothInput.buffer = Buffer.from(arrayBuffer);
  }

  const [mammothResult, ooxmlMeta, numberingMeta, tableMeta, textMeta] =
    await Promise.all([
      mammoth.convertToHtml(mammothInput, {
        ignoreEmptyParagraphs: false,
        styleMap: MAMMOTH_STYLE_MAP,
        transformDocument: transformDocumentForAlignment,
      }),
      extractParagraphMetaFromDocx(arrayBuffer).catch(() => []),
      extractNumberingFromDocx(arrayBuffer).catch(() => []),
      extractTableMetaFromDocx(arrayBuffer).catch(() => []),
      extractTextMetaFromDocx(arrayBuffer).catch(() => []),
    ]);

  const mammothMessages = mammothResult.messages.map((m) => ({
    code: m.type,
    message: m.message,
  }));

  const { html: withTextMeta, stats: textStats } = applyTextMetaToHtml(
    mammothResult.value,
    textMeta,
  );

  const { html: withParagraphMeta, stats: paragraphStats } =
    applyParagraphMetaToHtml(withTextMeta, ooxmlMeta);

  const { html: withNumbering, stats: numberingStats } = applyNumberingToHtml(
    withParagraphMeta,
    numberingMeta,
  );

  let { html, stats: tableStats } = applyTableMetaToHtml(
    withNumbering,
    tableMeta,
  );

  let messages = mammothMessages;

  if (!html.trim()) {
    const altChunk = await extractAltChunkContent(arrayBuffer).catch(() => null);
    if (altChunk?.html.trim()) {
      html = altChunk.html;
      messages = [
        ...mammothMessages,
        {
          code: "altchunk_fallback",
          message:
            "Imported from an HTML-based Word file (typical of LexGat DOCX downloads).",
        },
      ];
    }
  }

  return {
    html,
    mammothMessages: messages,
    paragraphStats: ooxmlMeta.length ? paragraphStats : EMPTY_PARAGRAPH_STATS,
    numberingStats: numberingMeta.length
      ? numberingStats
      : EMPTY_NUMBERING_STATS,
    tableStats: tableMeta.length ? tableStats : EMPTY_TABLE_STATS,
    textStats: textMeta.length ? textStats : EMPTY_TEXT_STATS,
  };
}
